import { inject, Injectable } from '@angular/core';
import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric';
import { LogService } from 'shared';

import { CredentialVaultService } from './credential-vault.service';
import { HapticsService } from '../../../services/haptics.service';

export interface BiometricStatus {
  isAvailable: boolean;
  biometryType: BiometryType;
  errorCode?: number;
}

@Injectable({
  providedIn: 'root',
})
export class BiometricService {
  private readonly vault = inject(CredentialVaultService);
  private readonly logger = inject(LogService);
  private readonly haptics = inject(HapticsService);

  async checkAvailability(): Promise<BiometricStatus> {
    try {
      const result = await NativeBiometric.isAvailable();
      return {
        isAvailable: result.isAvailable,
        biometryType: result.biometryType,
        errorCode: result.errorCode,
      };
    } catch (error) {
      this.logger.error(
        'Biometric availability check failed:',
        'BiometricService.checkAvailability',
        error,
      );
      return {
        isAvailable: false,
        biometryType: BiometryType.NONE,
      };
    }
  }

  async authenticate(reason?: string): Promise<boolean> {
    try {
      await this.haptics.light();
      await NativeBiometric.verifyIdentity({
        reason: reason ?? 'Authenticate to access DreamPay',
        title: 'Biometric Login',
        subtitle: 'Use your biometric to login',
        description: 'Touch the fingerprint sensor or look at the camera',
      });
      await this.haptics.success();
      return true;
    } catch (error) {
      this.logger.error('Biometric authentication failed:', 'BiometricService.authenticate', error);
      await this.haptics.error();
      return false;
    }
  }

  async enableBiometric(): Promise<void> {
    await this.vault.setBiometricEnabled(true);
  }

  async disableBiometric(): Promise<void> {
    await this.vault.setBiometricEnabled(false);
  }

  async isBiometricEnabled(): Promise<boolean> {
    return this.vault.isBiometricEnabled();
  }

  getBiometryTypeName(type: BiometryType): string {
    switch (type) {
      case BiometryType.FACE_ID:
        return 'Face ID';
      case BiometryType.TOUCH_ID:
        return 'Touch ID';
      case BiometryType.FINGERPRINT:
        return 'Fingerprint';
      case BiometryType.FACE_AUTHENTICATION:
        return 'Face Authentication';
      case BiometryType.IRIS_AUTHENTICATION:
        return 'Iris Authentication';
      case BiometryType.MULTIPLE:
        return 'Biometric';
      default:
        return 'Biometric';
    }
  }
}
